/**
 * Real "remaining this month" values for the mobile limit checks.
 *
 * useCapabilities used to hand out the plan CAPS (limits.voiceMinutesMonthly /
 * smsMonthly) under the names voiceMinutesRemaining / smsRemaining, so the
 * mobile "Call limit reached" / "Message limit reached" checks (`<= 0`) could
 * never fire on a used-up allowance. These come from GET /api/usage/status.
 */

export interface UsageStatusResponse {
  limits?: {
    /** null = no cap. */
    smsOutbound?: number | null;
    /** null = no cap. */
    voiceMinutesOutbound?: number | null;
    /** The plan sells overage: past the allowance it is charged, not stopped. */
    governed?: boolean;
  };
  usage?: { sms?: number; voice?: number };
}

/**
 * Units left, or null when there is no hard stop to warn about (no cap, an
 * overage-selling plan, or usage unknown). Never negative.
 */
export function remainingFrom(limit: number | null | undefined, used: number | null | undefined, governed?: boolean): number | null {
  if (governed) return null;
  if (limit === null || limit === undefined || !Number.isFinite(limit) || limit < 0) return null;
  if (used === null || used === undefined || !Number.isFinite(used)) return null;
  return Math.max(0, limit - used);
}

export function remainingFromUsageStatus(s: UsageStatusResponse | null | undefined): {
  voiceMinutesRemaining: number | null;
  smsRemaining: number | null;
} {
  return {
    voiceMinutesRemaining: remainingFrom(s?.limits?.voiceMinutesOutbound, s?.usage?.voice, s?.limits?.governed),
    smsRemaining: remainingFrom(s?.limits?.smsOutbound, s?.usage?.sms, s?.limits?.governed),
  };
}

/** "403: Monthly limit reached" → "Monthly limit reached" (apiRequest prefixes the HTTP status). */
export function stripStatusPrefix(message: string | null | undefined): string {
  return String(message ?? '').replace(/^\s*\d{3}:\s*/, '');
}

/** Does this API error mean a plan limit / allowance was hit (so an Upgrade CTA helps)? */
export function isLimitError(err: any): boolean {
  const code = String(err?.data?.code ?? err?.errorCode ?? err?.code ?? '');
  if (/LIMIT|QUOTA|CAP|ALLOWANCE|PLAN_REQUIRED|TRIAL_NO_PURCHASE|OUT_OF_CREDIT/i.test(code)) return true;
  const msg = stripStatusPrefix(err?.message).toLowerCase();
  return /limit (reached|exceeded)|reached your (monthly )?.*limit|allowance|upgrade/.test(msg);
}
